import * as React from 'react';
import { cn } from '../../utils/cn';
import { Avatar, AvatarImage, AvatarFallback } from '../avatar';
import { avatarVariants } from '../avatar/avatar';
import { type VariantProps } from 'class-variance-authority';

// Define interfaces for our component
interface Avatar {
    imageUrl?: string;
    name?: string;
}

/**
 * AvatarVariantProps is the type for the avatar props using the actual props from the component
 */
type AvatarVariantProps = VariantProps<typeof avatarVariants>;

/**
 * AvatarCirclesProps is the type for the avatar circles props
 */
export interface AvatarCirclesProps {
    className?: string;
    avatarClassName?: string;
    avatarUrls: Avatar[];
    size?: AvatarVariantProps['size'];
    border?: AvatarVariantProps['border'];
    customBorderColor?: string;
    status?: AvatarVariantProps['status'];
    maxAvatars?: number;
    /** Enable overlapping avatars with negative spacing */
    overlap?: boolean;
}

export const AvatarCircles = ({
    className,
    avatarClassName,
    avatarUrls,
    size = 'square40',
    border = 'light',
    customBorderColor,
    status = 'none',
    maxAvatars = 3,
    overlap = true,
}: AvatarCirclesProps) => {
    const avatarCount = avatarUrls.length;
    const remainingAvatars = avatarCount - maxAvatars;

    return (
        <div className={cn('flex items-center gap-2', className)}>
            <div
                className={cn(
                    'z-10 flex rtl:space-x-reverse',
                    overlap ? '-space-x-3' : 'gap-1'
                )}
            >
                {avatarUrls.slice(0, maxAvatars).map((url, index) => (
                    <Avatar
                        key={index}
                        className={avatarClassName}
                        size={size}
                        border={border}
                        customBorderColor={customBorderColor}
                        status={status}
                    >
                        <AvatarImage
                            src={url.imageUrl}
                            alt={url.name || `Avatar ${index + 1}`}
                            fit="cover"
                        />
                        <AvatarFallback size={mapFallBackTextSize(size)}>
                            {url.name ? getInitials(url.name) : `${index + 1}`}
                        </AvatarFallback>
                    </Avatar>
                ))}
                {(remainingAvatars ?? 0) > 0 && (
                    <Avatar
                        className={avatarClassName}
                        size={size}
                        border={border}
                        customBorderColor={customBorderColor}
                        status="none"
                    >
                        <AvatarFallback size={mapFallBackTextSize(size)}>
                            +{remainingAvatars}
                        </AvatarFallback>
                    </Avatar>
                )}
            </div>
        </div>
    );
};

// Helper function to get initials from a name
const getInitials = (name: string): string => {
    return name
        .split(' ')
        .map((part) => part.charAt(0))
        .join('')
        .toUpperCase()
        .substring(0, 2);
};

const mapFallBackTextSize = (
    size: AvatarVariantProps['size']
): 'xs' | 'sm' | 'md' | 'default' => {
    switch (size) {
        case 'square20':
            return 'xs';
        case 'square24':
            return 'xs';
        case 'square28':
            return 'sm';
        case 'square32':
            return 'md';
        case 'square40':
            return 'default';
        case 'square56':
            return 'default';
        case 'square64':
            return 'default';
        case 'square72':
            return 'default';
        default:
            return 'default';
    }
};
